import { useEffect, useState } from "react";
import { config } from "@/config";
import { loginWithGoogle } from "@/services/auth";
import { GoogleIcon } from "./GoogleIcon";

const App: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<string>("");
  const [messageType, setMessageType] = useState<"error" | "success">("error");

  const handleGoogleSignIn = (): void => {
    setMessage("");

    console.log("Google API check:", {
      google: !!window.google,
      accounts: !!window.google?.accounts,
      oauth2: !!window.google?.accounts?.oauth2,
      clientId: config.googleClientId,
    });

    if (!window.google?.accounts) {
      console.error("Google API not loaded");
      setMessage("Google API not loaded. Please refresh the page.");
      setMessageType("error");
      return;
    }

    if (!config.googleClientId) {
      console.error("Google Client ID not configured");
      setMessage(
        "Google Client ID is not configured. Please check your environment variables."
      );
      setMessageType("error");
      return;
    }

    setIsLoading(true);

    try {
      console.log(
        "Creating token client with client_id:",
        config.googleClientId
      );

      // Initialize Google Identity Services
      if (!window.google?.accounts?.id) {
        console.error("Google Identity Services not available");
        setMessage(
          "Google Identity Services not available. Please refresh the page."
        );
        setMessageType("error");
        setIsLoading(false);
        return;
      }

      // Use Google Identity Services to get id_token directly
      window.google.accounts.id.initialize({
        client_id: config.googleClientId,
        callback: async (response: { credential: string }) => {
          console.log("Google Identity Services callback received");

          // The credential is the id_token (JWT)
          const idToken = response.credential;

          if (!idToken) {
            console.error("No credential received from Google");
            setMessage("Failed to get Google token. Please try again.");
            setMessageType("error");
            setIsLoading(false);
            return;
          }

          try {
            await loginWithGoogle(idToken);
          } catch (error) {
            console.error("Login failed:", error);
            const errorMessage =
              error instanceof Error
                ? error.message
                : "Login failed. Please try again.";
            setMessage(errorMessage);
            setMessageType("error");
            setIsLoading(false);
          }
        },
      });

      // Trigger the Google Sign-In flow
      console.log("Prompting Google Sign-In...");
      window.google.accounts.id.prompt(() => {
        // This callback is called when the prompt is shown/dismissed
        // The actual credential comes in the initialize callback
      });
    } catch (error) {
      console.error("Error creating token client:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to initialize Google login.";
      setMessage(errorMessage);
      setMessageType("error");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const checkGoogleLoaded = (): void => {
      if (typeof window === "undefined") {
        return;
      }

      if (window.google?.accounts?.id) {
        console.log("Google Identity Services loaded successfully");
        return;
      }

      setTimeout(checkGoogleLoaded, 100);
    };

    // Wait for script to load
    const script = document.querySelector(
      'script[src*="accounts.google.com/gsi/client"]'
    );
    if (script) {
      script.addEventListener("load", () => {
        checkGoogleLoaded();
      });
    } else {
      checkGoogleLoaded();
    }
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
        <img
          src="/assets/main-logo.png"
          alt="Vammo logo"
          className="mx-auto mb-4 h-10"
        />
        <h1 className="text-2xl font-bold text-center mb-6">Login</h1>

        <button
          onClick={handleGoogleSignIn}
          disabled={isLoading}
          className="w-full bg-white border border-gray-300 rounded-lg px-4 py-3 flex items-center justify-center gap-3 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <GoogleIcon />

          <span className="text-gray-700 font-medium">
            {isLoading ? "Signing in..." : "Sign in with Google"}
          </span>
        </button>

        {message && (
          <div
            className={`mt-4 p-3 rounded-lg text-sm ${
              messageType === "error"
                ? "bg-red-50 text-red-700 border border-red-200"
                : "bg-green-50 text-green-700 border border-green-200"
            }`}
          >
            {message}
          </div>
        )}
      </div>
    </div>
  );
};

export default App;
